import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useBot } from '../../context/BotContext';
import { botApi } from '../../services/botApi';
import PositionDetailsDrawer from './PositionDetailsDrawer';

const { 
  FiTrendingUp, FiTrendingDown, FiAlertCircle, FiX, FiActivity, FiExternalLink, 
  FiRefreshCw, FiInfo, FiArchive, FiSearch, FiFilter, FiCheckSquare, FiSquare, 
  FiLayers, FiAward, FiAlertTriangle 
} = FiIcons;

const StatBox = ({ label, value, color = "text-white" }) => (
  <div className="flex flex-col">
    <span className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</span>
    <span className={`text-xs font-mono font-medium truncate ${color}`}>{value}</span>
  </div>
);

export default function PositionsView() {
  const {
    positions: paperPositions, closedTrades: paperClosed, settings, sellPosition, sellMultiplePositions,
    updatePositionSettings, updateLivePositionStrategy, refreshPrices, refreshLiveData, isRefreshing, lastRefreshed, notify,
    livePositions, liveTrades
  } = useBot();

  // In live mode the Positions tab shows the bot-managed live positions/history
  // (from Supabase); in paper mode it shows the simulated ones (localStorage).
  const isLive = settings.tradingMode === 'live';
  const positions = isLive ? livePositions : paperPositions;
  const closedTrades = isLive ? liveTrades : paperClosed;

  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'history'
  
  // Multi-select state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkSellPattern, setShowBulkSellPattern] = useState(false);
  
  // Confirmation Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingBulkPercentage, setPendingBulkPercentage] = useState(null);

  // History Filters
  const [historySearch, setHistorySearch] = useState('');
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all' | 'profit' | 'loss'

  // Live selected position for the drawer
  const activePosition = useMemo(() => {
    return positions.find(p => p.id === selectedPositionId) || null;
  }, [positions, selectedPositionId]);

  const formatPrice = (price) => {
    const p = parseFloat(price);
    if (!p) return '0';
    return p.toLocaleString('en-US', { maximumSignificantDigits: 6 });
  };

  const checkStopLossStatus = (pos, pnlPercentage, valueMultiplier) => {
    const effectiveHardSL = pos.customHardSL ?? settings.hardStopLoss;
    const effectiveTrailingSL = pos.customTrailingSL ?? settings.trailingStopLoss;
    const effectiveTrailingAct = pos.customTrailingAct ?? settings.trailingActivationMultiplier;
    // Match the engine: trailing is active once the PEAK reached the activation
    // multiplier, and stays active even if price pulls back below it.
    const peakMultiplier = pos.buyPrice > 0 ? pos.peakPrice / pos.buyPrice : 0;

    if (peakMultiplier >= effectiveTrailingAct) {
      const dropFromPeak = ((pos.peakPrice - pos.currentPrice) / pos.peakPrice) * 100;
      return { 
        active: true, 
        type: 'trailing', 
        text: `Trailing SL Active (-${dropFromPeak.toFixed(1)}% / -${effectiveTrailingSL}%)`, 
        color: 'text-blue-400' 
      };
    } else if (pnlPercentage < 0) {
      return { 
        active: true, 
        type: 'hard', 
        text: `Hard SL Risk (${pnlPercentage.toFixed(1)}% / -${effectiveHardSL}%)`, 
        color: 'text-orange-400' 
      };
    }
    return null;
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    if (selectedIds.length === positions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(positions.map(p => p.id));
    }
  };

  const handleBulkSellClick = (percentage) => {
    setPendingBulkPercentage(percentage);
    setShowConfirmModal(true);
  };

  const executeBulkSell = async () => {
    if (!pendingBulkPercentage) return;
    if (isLive) {
      // Live: fire a backend sell for each selected position (the bot's burner signs).
      const ids = [...selectedIds];
      notify(`Submitting ${ids.length} live sell(s)...`, 'info');
      for (const id of ids) {
        try { await botApi.sell({ positionId: id, percentage: pendingBulkPercentage }); }
        catch (e) { notify(`Live sell failed: ${e.message}`, 'error'); }
      }
    } else {
      sellMultiplePositions(selectedIds, pendingBulkPercentage);
    }
    setShowConfirmModal(false);
    setShowBulkSellPattern(false);
    setIsSelectMode(false);
    setSelectedIds([]);
    setPendingBulkPercentage(null);
  };

  // Filtered History Data
  const filteredHistory = useMemo(() => {
    return closedTrades.filter(trade => {
      const matchesSearch = trade.symbol.toLowerCase().includes(historySearch.toLowerCase());
      if (!matchesSearch) return false;
      if (historyFilter === 'profit') return trade.pnlPercentage >= 0;
      if (historyFilter === 'loss') return trade.pnlPercentage < 0;
      return true;
    });
  }, [closedTrades, historySearch, historyFilter]);

  return (
    <div className="p-4 relative min-h-[calc(100vh-140px)] lg:min-h-[calc(100vh-40px)]">
      {/* Header and Toggles */}
      <div className="flex flex-col mb-4">
        <h2 className="text-white font-bold text-xl mb-3">Paper Positions</h2>
        <div className="flex justify-between items-center border-b border-[#2c2c2e] pb-3">
          <div className="flex gap-1 bg-[#1c1c1d] p-1 rounded-lg border border-[#2c2c2e]">
            <button 
              onClick={() => { setViewMode('active'); setIsSelectMode(false); setSelectedIds([]); }}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${viewMode === 'active' ? 'bg-[#2c2c2e] text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
            >
              Active ({positions.length})
            </button>
            <button 
              onClick={() => { setViewMode('history'); setIsSelectMode(false); }}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${viewMode === 'history' ? 'bg-[#2c2c2e] text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
            >
              History ({closedTrades.length})
            </button>
          </div>
          <div className="flex items-center gap-2">
            {positions.length > 0 && viewMode === 'active' && (
              <button 
                onClick={() => { setIsSelectMode(!isSelectMode); setSelectedIds([]); }}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-colors border ${isSelectMode ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-[#1c1c1d] text-gray-400 border-[#2c2c2e] hover:text-white'}`}
              >
                {isSelectMode ? 'Cancel' : 'Select'}
              </button>
            )}
            <button
              onClick={isLive ? refreshLiveData : refreshPrices}
              disabled={isRefreshing}
              className="bg-[#2c2c2e] p-2 rounded-full text-gray-400 hover:text-white disabled:opacity-50 transition-colors border border-[#3c3c3e]"
            >
              <SafeIcon icon={FiRefreshCw} className={isRefreshing ? 'animate-spin text-blue-400' : ''} />
            </button>
          </div>
        </div>
        <div className="text-right text-[10px] text-gray-500 mt-2 h-4">
          {viewMode === 'active' && (lastRefreshed ? `Last updated: ${lastRefreshed.toLocaleTimeString()}` : 'Waiting for data...')}
        </div>
      </div>

      {/* Active Positions View */}
      {viewMode === 'active' && (
        positions.length === 0 ? (
          <div className="text-center text-gray-500 mt-16">
            <SafeIcon icon={FiAlertCircle} className="text-4xl mx-auto mb-2 opacity-50" />
            <p>No active paper positions found.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <AnimatePresence>
              {positions.map(pos => {
                const valueMultiplier = pos.currentPrice / pos.buyPrice;
                const pnlPercentage = (valueMultiplier - 1) * 100;
                const pnlSol = (pos.amountSol * valueMultiplier) - pos.amountSol;
                const isProfit = pnlPercentage >= 0;
                const slStatus = checkStopLossStatus(pos, pnlPercentage, valueMultiplier);
                
                const effectiveHardSL = pos.customHardSL ?? settings.hardStopLoss;
                const effectiveTrailingSL = pos.customTrailingSL ?? settings.trailingStopLoss;
                const effectiveTrailingAct = pos.customTrailingAct ?? settings.trailingActivationMultiplier;

                const hardStopPrice = pos.buyPrice * (1 - effectiveHardSL / 100);
                const isTrailingActive = pos.buyPrice > 0 && (pos.peakPrice / pos.buyPrice) >= effectiveTrailingAct;
                const trailingStopPrice = isTrailingActive ? pos.peakPrice * (1 - effectiveTrailingSL / 100) : null;

                // USD display: convert native (SOL) thresholds using the current SOL/USD rate
                // (priceUsd/priceNative). Falls back to SOL when USD prices aren't available.
                const solToUsd = pos.currentPrice > 0 && pos.currentPriceUsd ? pos.currentPriceUsd / pos.currentPrice : null;
                const toUsd = (native) => (solToUsd != null && native != null) ? native * solToUsd : null;
                const peakUsd = pos.peakPriceUsd ?? toUsd(pos.peakPrice);
                const hardStopUsd = toUsd(hardStopPrice);
                const trailingStopUsd = trailingStopPrice != null ? toUsd(trailingStopPrice) : null;
                
                const isSelected = selectedIds.includes(pos.id);
                const milestonesCount = (pos.milestonesHit || []).length;
                const lossMilestonesCount = (pos.lossMilestonesHit || []).length;

                return (
                  <motion.div 
                    key={pos.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    onClick={() => isSelectMode ? toggleSelection(pos.id) : null}
                    className={`bg-[#1c1c1d] border rounded-xl p-4 shadow-lg transition-colors relative ${isSelectMode ? 'cursor-pointer' : ''} ${isSelectMode && isSelected ? 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.15)] bg-blue-500/5' : 'border-[#2c2c2e]'}`}
                  >
                    {isSelectMode && (
                      <div className="absolute top-4 right-4 z-10 pointer-events-none">
                        <SafeIcon icon={isSelected ? FiCheckSquare : FiSquare} className={`text-xl ${isSelected ? 'text-blue-500' : 'text-gray-500'}`} />
                      </div>
                    )}

                    <div className="flex justify-between items-start mb-2 pr-6">
                      <div>
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <h3 className="text-white font-bold text-lg">${pos.symbol}</h3>
                          {pos.isReal && (
                            <span className="flex items-center gap-1 bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded border border-green-500/30 text-[10px] font-bold uppercase tracking-wider">
                              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" /> Live
                            </span>
                          )}
                          <a
                            href={`https://dexscreener.com/solana/${pos.address}`} 
                            target="_blank" 
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-gray-500 hover:text-blue-400 transition-colors flex items-center gap-1 text-xs bg-[#2c2c2e] px-1.5 py-0.5 rounded border border-[#3c3c3e]"
                          >
                            Chart <SafeIcon icon={FiExternalLink} className="text-[10px]" />
                          </a>
                          {milestonesCount > 0 && (
                            <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded border border-green-500/30 flex items-center gap-1">
                              <SafeIcon icon={FiAward} /> {milestonesCount}
                            </span>
                          )}
                          {lossMilestonesCount > 0 && (
                            <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded border border-red-500/30 flex items-center gap-1">
                              <SafeIcon icon={FiTrendingDown} /> {lossMilestonesCount}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs font-mono">Inv: {pos.amountSol.toFixed(2)} SOL</p>
                      </div>
                      <div className={`text-right ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                        <div className="flex items-center justify-end gap-1 font-bold text-lg">
                          <SafeIcon icon={isProfit ? FiTrendingUp : FiTrendingDown} />
                          {isProfit ? '+' : ''}{pnlPercentage.toFixed(2)}%
                        </div>
                        <p className="text-xs font-mono font-medium bg-black/30 rounded px-1.5 py-0.5 inline-block mt-1">
                          Val: {(pos.amountSol * valueMultiplier).toFixed(3)} SOL
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-y-3 gap-x-2 mt-3 mb-4 bg-[#0a0a0b] p-3 rounded-lg border border-[#2c2c2e]">
                      <StatBox label="Entry Price" value={pos.buyPriceUsd ? `$${formatPrice(pos.buyPriceUsd)}` : formatPrice(pos.buyPrice)} />
                      <StatBox label="Current Price" value={pos.currentPriceUsd ? `$${formatPrice(pos.currentPriceUsd)}` : formatPrice(pos.currentPrice)} color={isProfit ? 'text-green-400' : 'text-red-400'} />
                      <StatBox label="Peak Price" value={peakUsd != null ? `$${formatPrice(peakUsd)}` : formatPrice(pos.peakPrice)} />
                      <StatBox label="P&L (SOL)" value={`${isProfit ? '+' : ''}${pnlSol.toFixed(4)}`} color={isProfit ? 'text-green-400' : 'text-red-400'} />
                      <StatBox label={`Hard Stop (-${effectiveHardSL}%)`} value={hardStopUsd != null ? `$${formatPrice(hardStopUsd)}` : `${formatPrice(hardStopPrice)} SOL`} color="text-orange-400" />
                      <StatBox label={`Trail Stop (-${effectiveTrailingSL}%)`} value={isTrailingActive ? (trailingStopUsd != null ? `$${formatPrice(trailingStopUsd)}` : `${formatPrice(trailingStopPrice)} SOL`) : `Wait ${effectiveTrailingAct}x`} color={isTrailingActive ? "text-blue-400" : "text-gray-600"} />
                    </div>

                    {slStatus && (
                      <div className="bg-[#2c2c2e]/50 rounded-lg p-2 mb-3 flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${slStatus.type === 'trailing' ? 'bg-blue-400' : 'bg-orange-400'} animate-pulse`}></div>
                        <span className={`text-[11px] font-medium ${slStatus.color}`}>
                          {slStatus.text}
                        </span>
                      </div>
                    )}

                    {/* Max loss if hard SL fires from original entry */}
                    {!isProfit && (
                      <div className="mb-2 flex items-center justify-between text-[10px] bg-red-500/5 border border-red-500/15 rounded-lg px-2.5 py-1.5">
                        <span className="text-gray-500 flex items-center gap-1">
                          <SafeIcon icon={FiAlertTriangle} className="text-[9px] text-red-400" /> Max loss if hard SL fires:
                        </span>
                        <span className="text-red-400 font-mono font-bold">
                          -{(pos.amountSol * (effectiveHardSL / 100)).toFixed(4)} SOL
                        </span>
                      </div>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSelectMode) {
                          toggleSelection(pos.id);
                        } else {
                          setSelectedPositionId(pos.id);
                        }
                      }}
                      className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors border ${isSelectMode && isSelected ? 'bg-blue-500 text-white border-blue-500' : 'bg-[#2c2c2e] hover:bg-[#3c3c3e] text-white border-[#3c3c3e]'}`}
                    >
                      {isSelectMode ? (isSelected ? 'Selected' : 'Select Position') : 'Detailed Analysis'}
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )
      )}

      {/* Bulk Action Bar - Fixed Bottom Overlay */}
      <AnimatePresence>
        {isSelectMode && selectedIds.length > 0 && viewMode === 'active' && (
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="fixed bottom-[65px] left-0 right-0 max-w-md mx-auto px-4 z-30"
          >
            <div className="bg-[#1c1c1d] border border-[#3c3c3e] rounded-2xl shadow-[0_-5px_20px_rgba(0,0,0,0.5)] p-3 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button 
                  onClick={selectAll}
                  className="text-xs text-blue-400 font-medium px-2 py-1 bg-blue-500/10 rounded border border-blue-500/20"
                >
                  {selectedIds.length === positions.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-gray-400 text-xs">|</span>
                <span className="text-white font-bold text-sm">{selectedIds.length} Selected</span>
              </div>
              <button 
                onClick={() => setShowBulkSellPattern(true)}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-bold transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2"
              >
                <SafeIcon icon={FiLayers} /> Bulk Sell
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Closed Trades / History View */}
      {viewMode === 'history' && (
        <div className="flex flex-col gap-3">
          {/* Filters */}
          {closedTrades.length > 0 && (
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs" />
                <input 
                  type="text" 
                  placeholder="Search token..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  className="w-full bg-[#1c1c1d] border border-[#2c2c2e] rounded-lg py-2 pl-8 pr-3 text-white text-xs focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
              <div className="relative">
                <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none" />
                <select 
                  value={historyFilter}
                  onChange={(e) => setHistoryFilter(e.target.value)}
                  className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-lg py-2 pl-8 pr-8 text-white text-xs focus:outline-none focus:border-blue-500 appearance-none cursor-pointer"
                >
                  <option value="all">All</option>
                  <option value="profit">Winners</option>
                  <option value="loss">Losers</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 text-[10px]">
                  ▼
                </div>
              </div>
            </div>
          )}

          {closedTrades.length === 0 ? (
            <div className="text-center text-gray-500 mt-16">
              <SafeIcon icon={FiArchive} className="text-4xl mx-auto mb-2 opacity-50" />
              <p>No closed trades found.</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center text-gray-500 mt-16">
              <SafeIcon icon={FiSearch} className="text-4xl mx-auto mb-2 opacity-50" />
              <p>No trades match your filters.</p>
            </div>
          ) : (
            <AnimatePresence>
              {filteredHistory.map(trade => {
                const isProfit = trade.pnlPercentage >= 0;
                return (
                  <motion.div 
                    key={trade.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4 opacity-90 hover:opacity-100 transition-opacity relative overflow-hidden"
                  >
                    {/* Visual Profit/Loss indicator edge */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${isProfit ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    
                    <div className="flex justify-between items-start mb-2 pl-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-bold text-md">${trade.symbol}</h3>
                          <a 
                            href={`https://dexscreener.com/solana/${trade.address}`} 
                            target="_blank" 
                            rel="noreferrer"
                            className="text-gray-500 hover:text-blue-400 transition-colors flex items-center gap-1 text-[10px] bg-[#2c2c2e] px-1 py-0.5 rounded border border-[#3c3c3e]"
                          >
                            Chart <SafeIcon icon={FiExternalLink} className="text-[8px]" />
                          </a>
                        </div>
                        <p className="text-gray-500 text-[10px]">
                          {new Date(trade.closedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className={`text-right ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                        <div className="font-bold text-md flex items-center justify-end gap-1">
                          <SafeIcon icon={isProfit ? FiTrendingUp : FiTrendingDown} className="text-sm" />
                          {isProfit ? '+' : ''}{trade.pnlPercentage.toFixed(2)}%
                        </div>
                        <p className="text-[11px] font-mono mt-0.5 bg-black/30 px-1 rounded inline-block">
                          {isProfit ? '+' : ''}{trade.pnlSol.toFixed(4)} SOL
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3 bg-[#0a0a0b] p-2 rounded-lg border border-[#2c2c2e] pl-4">
                      <StatBox label="Entry Price" value={trade.buyPriceUsd != null ? `$${formatPrice(trade.buyPriceUsd)}` : formatPrice(trade.buyPrice)} />
                      <StatBox label="Sold At" value={trade.sellPriceUsd != null ? `$${formatPrice(trade.sellPriceUsd)}` : formatPrice(trade.sellPrice)} />
                    </div>
                    <div className="mt-3 text-[11px] text-gray-400 flex items-center gap-1.5 bg-[#2c2c2e]/50 px-2.5 py-1.5 rounded w-fit ml-2 border border-[#3c3c3e]/50">
                      <SafeIcon icon={FiInfo} className={isProfit ? "text-green-500" : "text-orange-500"} />
                      {trade.reason}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      )}

      {/* Detailed Position Drawer */}
      <PositionDetailsDrawer
        position={activePosition}
        isOpen={!!activePosition && !isSelectMode && viewMode === 'active'}
        onClose={() => setSelectedPositionId(null)}
        settings={settings}
        notify={notify}
        onSell={(percent) => {
          if (activePosition) {
            sellPosition(activePosition.id, percent);
            setSelectedPositionId(null);
          }
        }}
        onSaveStrategy={(newSettings) => {
          if (!activePosition) return;
          if (isLive) updateLivePositionStrategy(activePosition.id, newSettings);
          else updatePositionSettings(activePosition.id, newSettings);
        }}
      />

      {/* Bulk Sell Drawer Overlay */}
      <AnimatePresence>
        {showBulkSellPattern && isSelectMode && viewMode === 'active' && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowBulkSellPattern(false)}
              className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-[#1c1c1d] rounded-t-2xl z-[60] border-t border-[#2c2c2e] pb-safe"
            >
              <div className="p-5">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-white font-bold text-xl">Bulk Sell ({selectedIds.length})</h3>
                    <p className="text-gray-400 text-sm">Choose paper portion to close for all selected</p>
                  </div>
                  <button 
                    onClick={() => setShowBulkSellPattern(false)}
                    className="w-8 h-8 bg-[#2c2c2e] rounded-full flex items-center justify-center text-gray-400 hover:text-white"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[25, 50, 75, 100].map(percent => (
                    <button 
                      key={percent}
                      onClick={() => handleBulkSellClick(percent)}
                      className={`py-4 rounded-xl font-bold text-lg transition-colors border ${percent === 100 ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20' : 'bg-[#2c2c2e] text-white border-[#3c3c3e] hover:bg-[#3c3c3e]'}`}
                    >
                      {percent}%
                    </button>
                  ))}
                </div>
                <button 
                  onClick={() => setShowBulkSellPattern(false)}
                  className="w-full text-gray-500 hover:text-gray-300 font-medium py-3"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bulk Action Confirmation Modal */}
      <AnimatePresence>
        {showConfirmModal && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-[70] backdrop-blur-sm"
              onClick={() => setShowConfirmModal(false)}
            />
            <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 pointer-events-none">
              <motion.div 
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                className="bg-[#1c1c1d] border border-[#3c3c3e] rounded-2xl p-6 max-w-sm w-full pointer-events-auto shadow-[0_10px_40px_rgba(0,0,0,0.5)]"
              >
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-14 h-14 bg-orange-500/10 rounded-full flex items-center justify-center mb-4 border border-orange-500/20">
                    <SafeIcon icon={FiAlertTriangle} className="text-3xl text-orange-400" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">Confirm Bulk Action</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    You are about to sell <strong className="text-white">{pendingBulkPercentage}%</strong> of <strong className="text-white">{selectedIds.length}</strong> selected positions. This action will be executed instantly and cannot be undone.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 py-3 rounded-xl font-medium text-gray-400 hover:text-white bg-[#2c2c2e] hover:bg-[#3c3c3e] transition-colors border border-[#3c3c3e]"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={executeBulkSell}
                    className="flex-1 py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/20 border border-blue-500"
                  >
                    Execute Sell
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}